import { useMutation, useQueryClient } from '@tanstack/react-query'

import { templateEditError, templatesApi } from '@/lib/api'
import type { TemplateField } from '@/types/domain'

import { cockpitKeys, type CockpitState } from '../useCockpit'
import { reorderFields, sameFieldSet } from './templateTabModel'

/**
 * The five writes the Vorlage tab makes, each with the invalidations it owes
 * the rest of the cockpit.
 *
 * Adding or removing a layout changes the document counts as well as the
 * template (the delete drops the matched documents' layout, the card grid and
 * the Dokumente tab's Layout column both read it), so those two invalidate
 * counts too.
 */

/** A save refused because somebody else changed the template meanwhile. */
export class ConcurrentTemplateEdit extends Error {
  constructor() {
    super('concurrent template edit')
    this.name = 'ConcurrentTemplateEdit'
  }
}

/** The German sentence for a refusal: the gateway's mapped editor codes
 *  first, then the two cases they do not cover. */
export function editErrorMessage(error: unknown): string {
  if (error instanceof ConcurrentTemplateEdit) {
    return 'Die Vorlage wurde inzwischen an anderer Stelle geändert. Die Ansicht ist jetzt aktuell — bitte prüfen Sie die Reihenfolge und versuchen Sie es noch einmal.'
  }
  const known = templateEditError(error)
  if (known) return known.message
  const status = (error as { status?: number } | null)?.status
  if (status === 403) return 'Sie dürfen diese Vorlage nicht ändern.'
  return 'Das hat leider nicht geklappt. Bitte versuchen Sie es noch einmal.'
}

export interface AddPageInput {
  file: File
  label?: string
  variantOf?: number
}

export type TemplateEdits = ReturnType<typeof useTemplateEdits>

export function useTemplateEdits(cockpit: CockpitState) {
  const { templateId, invalidate } = cockpit
  const queryClient = useQueryClient()

  const afterPageChange = async () => {
    await Promise.all([invalidate.template(), invalidate.counts()])
  }

  const addPage = useMutation({
    mutationFn: (input: AddPageInput) =>
      templatesApi.addPage(templateId, {
        file: input.file,
        filename: input.file.name,
        ...(input.variantOf != null ? { variantOf: input.variantOf } : {}),
        ...(input.label ? { label: input.label } : {}),
      }),
    onSuccess: afterPageChange,
  })

  const setPageLabel = useMutation({
    mutationFn: ({ page, label }: { page: number; label: string }) =>
      // An empty name clears the label, which puts the card back to "Seite n"
      // — the endpoint distinguishes an explicit null from an absent key.
      templatesApi.updatePage(templateId, page, label ? { label } : { clearLabel: true }),
    onSuccess: () => invalidate.template(),
  })

  const deletePage = useMutation({
    mutationFn: ({ page, force }: { page: number; force?: boolean }) =>
      templatesApi.deletePage(templateId, page, { force }),
    onSuccess: afterPageChange,
  })

  const deleteField = useMutation({
    mutationFn: (fieldId: string) => templatesApi.deleteField(templateId, fieldId),
    onSuccess: () => invalidate.template(),
  })

  /**
   * Moving a field one place in the export order.
   *
   * `PATCH /v1/templates/:id` with `fields` replaces the whole field set and
   * detects no conflict, so this re-reads the template immediately before
   * writing and refuses when the field set it was rendered from is no longer
   * the one on the server: sending the stale set would delete a colleague's
   * new field without a word. `pageList` is deliberately not sent — pages
   * missing from the payload are left untouched, so a reorder cannot damage
   * a box.
   */
  const moveField = useMutation({
    mutationFn: async ({
      fieldId,
      direction,
      known,
    }: {
      fieldId: string
      direction: 'up' | 'down'
      known: TemplateField[]
    }) => {
      const fresh = await queryClient.fetchQuery({
        queryKey: cockpitKeys.template(templateId),
        queryFn: () => templatesApi.get(templateId),
        staleTime: 0,
      })
      if (!sameFieldSet(known, fresh.fields)) throw new ConcurrentTemplateEdit()
      await templatesApi.patch(templateId, { fields: reorderFields(fresh.fields, fieldId, direction) })
    },
    onSettled: () => invalidate.template(),
  })

  return { addPage, setPageLabel, deletePage, deleteField, moveField }
}
